import { Loading } from '@nike/frame-component-library';
import { Delete, Edit } from '@nike/nike-design-system-icons';
import React, { useState } from 'react';
import ReactSelect from 'react-select';
import ReactTable from 'react-table';

import 'react-table/react-table.css';

import { loadState } from '../../../utils/local-storage';
import {
  scsDefinitionGet, scsScheduleDelete, scsScheduleEdit, scsScheduleGet,
} from '../../../utils/service-calls/scs';
import { isUserAllowed } from '../../../utils/tab-permissions';
import { ButtonBlack, ButtonRed } from '../../reusable';
import Modal from '../../reusable/CommonModal/Modal';

import Overlay from './Overlay';

const config = require('../../../config')();

const scsKeyGroups = ['SCS.ALL', 'SCS.ASSIST', 'SCS.LINK', 'SCS.NPOS', 'SCS.OTHER', 'SCS.POS', 'SCS.PROMOTE', 'SCS.RL', 'SCS.RISE', 'SCS.SFS', 'SCS.SIM', 'SCS.SCO', 'SCS.TRACK'];

const securityGroupMap = new Map([
  ['AIC Settings', 'SCS.ASSIST'],
  ['Assist Settings', 'SCS.ASSIST'],
  ['NPOS Settings', 'SCS.NPOS'],
  ['NSP Settings', 'SCS.ASSIST'],
  ['Nike+ Link Settings', 'SCS.LINK'],
  ['Other Settings', 'SCS.OTHER'],
  ['POS Settings', 'SCS.POS'],
  ['Promote App Settings', 'SCS.PROMOTE'],
  ['RISE Settings', 'SCS.RISE'],
  ['Retail Launchpad', 'SCS.RL'],
  ['SCORE Settings', 'SCS.SFS'],
  ['SIM Settings', 'SCS.SIM'],
  ['Self Checkout Settings', 'SCS.SCO'],
  ['Track Settings', 'SCS.TRACK'],
]);

const formatScheduledTime = (isoString) => {
  if (!isoString) return '--';
  const [datePart, timePart] = isoString.split('T');
  if (!datePart) return '--';
  const [year, month, day] = datePart.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[parseInt(month, 10) - 1] || month;
  const time = timePart ? timePart.slice(0, 5) : '';
  return `${monthName} ${parseInt(day, 10)}, ${year}, ${time} PT`;
};

const toLocalDatetime = (isoString) => {
  if (!isoString) return '';
  return isoString.slice(0, 16);
};

const toISODatetime = (localString) => {
  if (!localString) return '';
  return `${localString}:00`;
};

const groupSchedules = (schedules) => {
  const groupMap = new Map();
  schedules.forEach((schedule) => {
    const group = groupMap.get(schedule.groupId) || [];
    group.push(schedule);
    groupMap.set(schedule.groupId, group);
  });

  return Array.from(groupMap.values()).map((group) => {
    if (group.length === 1) return group[0];
    const first = group[0];
    return {
      createdBy: first.createdBy,
      groupId: first.groupId,
      isGroup: true,
      key: first.key,
      lastModifiedBy: first.lastModifiedBy,
      scheduleCount: group.length,
      scheduledStartTime: first.scheduledStartTime,
      schedules: group,
    };
  });
};

const getColumns = (onDeleteClick, onEditClick) => [
  {
    accessor: 'key',
    Header: 'KEY NAME',
    id: 'keyName',
  },
  {
    // eslint-disable-next-line react/prop-types
    Cell: ({ original }) => formatScheduledTime(original.scheduledStartTime),
    Header: 'SCHEDULED FOR',
    id: 'scheduledFor',
  },
  {
    accessor: (d) => d.createdBy || d.lastModifiedBy || '--',
    Header: 'SUBMITTED BY',
    id: 'submittedBy',
  },
  {
    /* eslint-disable react/prop-types */
    Cell: ({ original }) => {
      const counts = original.isGroup
        ? original.schedules.reduce((acc, s) => {
          acc[s.status] = (acc[s.status] || 0) + 1;
          return acc;
        }, {})
        : { [original.status]: 1 };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(counts).map(([status, count]) => (
            <span
              key={status}
              style={{
                backgroundColor: { COMPLETED: '#e8f5e9', FAILED: '#fdecea', PENDING: '#fff3e0' }[status] || '#e0e0e0',
                borderRadius: '10px',
                color: { COMPLETED: '#2e7d32', FAILED: '#d32f2f', PENDING: '#d43f21' }[status],
                fontSize: '13px',
                fontWeight: 600,
                padding: '2px 8px',
                textAlign: 'center',
              }}
            >
              {count} {status}
            </span>
          ))}
        </div>
      );
    },
    /* eslint-enable react/prop-types */
    Header: 'STATUS',
    id: 'status',
  },
  {
    /* eslint-disable react/prop-types */
    Cell: ({ original }) => {
      const isPending = original.isGroup
        ? original.schedules.some((s) => s.status === 'PENDING')
        : original.status === 'PENDING';
      return (
        <div style={{ display: 'flex', gap: '12px' }}>
          <Edit
            size="s"
            style={{ cursor: isPending ? 'pointer' : 'not-allowed', opacity: isPending ? 1 : 0.3 }}
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              if (isPending) onEditClick(original);
            }}
          />
          <Delete
            size="s"
            style={{ cursor: isPending ? 'pointer' : 'not-allowed', opacity: isPending ? 1 : 0.3 }}
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              if (isPending) onDeleteClick(original);
            }}
          />
        </div>
      );
    },
    /* eslint-enable react/prop-types */
    Header: 'ACTIONS',
    id: 'actions',
    sortable: false,
    width: 100,
  },
];

const DEFAULT_STATUS = 'PENDING';

const getStatusCounts = (schedules) => {
  const counts = {};
  schedules.forEach((s) => {
    counts[s.status] = (counts[s.status] || 0) + 1;
  });
  return counts;
};

const Main = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(DEFAULT_STATUS);
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlaySchedules, setOverlaySchedules] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editScheduledTime, setEditScheduledTime] = useState('');
  const [editJustification, setEditJustification] = useState('');
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const { accessToken, groups: userGroups } = loadState('auth') || {};
  const scsGroups = scsKeyGroups.filter((group) => isUserAllowed(userGroups || [], group));

  const fetchSchedules = async (region) => {
    setFetching(true);
    setFetched(false);
    setError(null);
    try {
      const [response, defResponse] = await Promise.all([
        scsScheduleGet(accessToken, region),
        scsDefinitionGet(accessToken, region),
      ]);
      const definitions = defResponse.body?.objects || [];
      const keyGroupMap = {};
      definitions.forEach((def) => {
        keyGroupMap[def.key] = def.group || 'Other Settings';
      });
      const allSchedules = response.body?.schedules || [];
      const filteredSchedules = allSchedules.filter((s) => {
        const keyGroup = keyGroupMap[s.key];
        return scsGroups.includes('SCS.ALL') || scsGroups.includes(securityGroupMap.get(keyGroup));
      });
      setSchedules(filteredSchedules);
    } catch (err) {
      setError('Failed to fetch scheduled changes. Please try again.');
      setSchedules([]);
    } finally {
      setFetching(false);
      setFetched(true);
    }
  };

  const handleRegionChange = (regionOption) => {
    setSelectedRegion(regionOption);
    setSelectedStatus(DEFAULT_STATUS);
    fetchSchedules(regionOption.value);
  };

  const handleRowClick = (row) => {
    const rowSchedules = row.isGroup ? row.schedules : [row];
    setOverlaySchedules(rowSchedules);
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setOverlaySchedules([]);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setDeleteError(null);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await scsScheduleDelete(accessToken, deleteTarget.groupId, selectedRegion.value);
      setDeleteModalVisible(false);
      setDeleteTarget(null);
      setSuccessMessage('Schedule deleted successfully.');
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete schedule. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleEditClick = (row) => {
    const rowSchedules = row.isGroup ? row.schedules : [row];
    setEditTarget({ ...row, schedules: rowSchedules });
    setEditValue(String(rowSchedules[0].value));
    setEditScheduledTime(rowSchedules[0].scheduledStartTime);
    setEditJustification('');
    setEditError(null);
    setEditModalVisible(true);
  };

  const handleEditConfirm = async () => {
    if (!editTarget) return;
    setEditing(true);
    setEditError(null);
    const body = {
      schedules: editTarget.schedules.map((s) => ({
        justification: editJustification || undefined,
        key: s.key,
        scheduledStartTime: toISODatetime(toLocalDatetime(editScheduledTime)),
        scheduleId: s.scheduleId,
        value: editValue,
      })),
    };
    try {
      await scsScheduleEdit(accessToken, editTarget.groupId, selectedRegion.value, body);
      setEditModalVisible(false);
      setEditTarget(null);
      setSuccessMessage('Schedule updated successfully.');
    } catch (err) {
      setEditError(err?.message || 'Failed to update schedule. Please try again.');
    } finally {
      setEditing(false);
    }
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setEditTarget(null);
    setEditValue('');
    setEditScheduledTime('');
    setEditJustification('');
    setEditError(null);
  };

  const getLatestModified = (row) => {
    if (row.isGroup) {
      return Math.max(...row.schedules.map((s) => new Date(s.lastModifiedOn || 0).getTime()));
    }
    return new Date(row.lastModifiedOn || 0).getTime();
  };

  const allGroupedRows = groupSchedules(schedules)
    .sort((a, b) => getLatestModified(b) - getLatestModified(a));
  const groupedRows = selectedStatus
    ? allGroupedRows.filter((row) => (row.isGroup
      ? row.schedules.some((s) => s.status === selectedStatus)
      : row.status === selectedStatus))
    : allGroupedRows;
  const statusCounts = getStatusCounts(schedules);
  const statusKeys = Object.keys(statusCounts);
  const filteredCount = groupedRows.reduce(
    (acc, row) => acc + (row.isGroup ? row.schedules.length : 1), 0,
  );
  const badgeLabel = selectedStatus
    ? `${filteredCount} matching`
    : `${schedules.length} total`;

  return (
    <main className="ncss-col-sm-12 ta-sm-l">
      <section className="ncss-row">
        <article className="ncss-col-sm-6 va-sm-t ta-sm-l pt4-sm">
          AWS Region
          <ReactSelect
            classNamePrefix="react-select"
            clearable={false}
            id="scsScheduleRegion"
            options={config.scsRegionOptions}
            styles={{
              container: (styles) => ({ ...styles, zIndex: 100 }),
              control: (styles) => ({
                ...styles,
                borderColor: 'lightgrey',
                padding: '0px 0px 0px 3px',
              }),
            }}
            value={selectedRegion}
            onChange={handleRegionChange}
          />
        </article>
      </section>

      {selectedRegion && fetched && !error && statusKeys.length > 0 && (
        <section className="ncss-col-sm-12 pt4-sm" style={{ display: 'flex', gap: '20px' }}>
          <label
            htmlFor="status-all"
            style={{
              alignItems: 'center', cursor: 'pointer', display: 'flex', gap: '6px',
            }}
          >
            <input
              checked={!selectedStatus}
              id="status-all"
              name="scheduleStatus"
              type="radio"
              onChange={() => setSelectedStatus('')}
            />
            All
            <span style={{
              backgroundColor: '#e0e0e0',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 600,
              padding: '2px 8px',
            }}
            >
              {schedules.length}
            </span>
          </label>
          {statusKeys.map((status) => (
            <label
              key={status}
              htmlFor={`status-${status}`}
              style={{
                alignItems: 'center', cursor: 'pointer', display: 'flex', gap: '6px',
              }}
            >
              <input
                checked={selectedStatus === status}
                id={`status-${status}`}
                name="scheduleStatus"
                type="radio"
                onChange={() => setSelectedStatus(status)}
              />
              {status}
              <span style={{
                backgroundColor: { COMPLETED: '#e8f5e9', FAILED: '#fdecea', PENDING: '#fff3e0' }[status] || '#e0e0e0',
                borderRadius: '10px',
                color: { COMPLETED: '#2e7d32', FAILED: '#d32f2f', PENDING: '#d43f21' }[status],
                fontSize: '12px',
                fontWeight: 600,
                padding: '2px 8px',
              }}
              >
                {statusCounts[status]}
              </span>
            </label>
          ))}
        </section>
      )}

      {selectedRegion && (
        <article className="ncss-col-sm-12 pt6-sm">
          <div style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
          >
            <h4 className="headline-4">Scheduled Config Changes</h4>
            {fetched && !error && (
              <span style={{ color: '#d43f21', fontSize: '14px', fontWeight: 600 }}>
                {badgeLabel}
              </span>
            )}
          </div>

          {fetching && <Loading />}

          {error && <p className="body-2 text-color-error pt2-sm">{error}</p>}

          {!fetching && fetched && !error && (
            groupedRows.length > 0 ? (
              <ReactTable
                className="-striped -highlight"
                columns={getColumns(handleDeleteClick, handleEditClick).map((col) => ({
                  ...col,
                  headerStyle: {
                    background: '#333',
                    color: 'white',
                    fontWeight: 600,
                    padding: '10px 8px',
                    textAlign: 'left',
                  },
                  style: { padding: '12px 8px', textAlign: 'left' },
                }))}
                data={groupedRows}
                defaultPageSize={groupedRows.length > 10 ? 10 : groupedRows.length}
                getTdProps={() => ({
                  style: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
                })}
                getTrProps={(state, rowInfo) => ({
                  onClick: () => rowInfo && handleRowClick(rowInfo.original),
                  style: { cursor: 'pointer' },
                })}
                showPagination={groupedRows.length > 10}
              />
            ) : (
              <p className="body-2 text-color-secondary pt2-sm">No scheduled changes found.</p>
            )
          )}
        </article>
      )}

      <Overlay
        isVisible={overlayVisible}
        schedules={overlaySchedules}
        onClose={closeOverlay}
      />

      <Modal
        buttonName="CANCEL"
        isOpen={deleteModalVisible}
        loading={deleting}
        title="Confirm Delete"
        onClose={closeDeleteModal}
      >
        {deleteTarget && (
          <p>
            This will delete{' '}
            <strong>{deleteTarget.isGroup ? deleteTarget.scheduleCount : 1}</strong>
            {' '}scheduled change(s) for key{' '}
            <strong>{deleteTarget.key}</strong>.
            {' '}Are you sure?
          </p>
        )}
        {deleteError && (
          <p className="text-color-error" style={{ marginTop: '8px' }}>{deleteError}</p>
        )}
        <ButtonRed
          confirm={false}
          isDisabled={deleting}
          label="DELETE"
          onClick={handleDeleteConfirm}
        />
      </Modal>

      <Modal
        isOpen={editModalVisible}
        loading={editing}
        title="Edit Scheduled Changes"
        onClose={closeEditModal}
      >
        {editTarget && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
              Key: <span className="text-color-accent">{editTarget.key}</span>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label
                htmlFor="edit-value"
                style={{ display: 'flex', flex: 1, flexDirection: 'column' }}
              >
                Value
                <input
                  id="edit-value"
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    marginTop: '4px',
                    padding: '8px',
                  }}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                />
              </label>
              <label
                htmlFor="edit-time"
                style={{ display: 'flex', flex: 1, flexDirection: 'column' }}
              >
                Scheduled Time
                <input
                  id="edit-time"
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    marginTop: '4px',
                    padding: '8px',
                  }}
                  type="datetime-local"
                  value={toLocalDatetime(editScheduledTime)}
                  onChange={(e) => setEditScheduledTime(toISODatetime(e.target.value))}
                />
              </label>
            </div>
          </div>
        )}
        <label
          htmlFor="edit-justification"
          style={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}
        >
          Justification (optional)
          <textarea
            id="edit-justification"
            rows={3}
            style={{
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              marginTop: '4px',
              padding: '8px',
              resize: 'vertical',
            }}
            value={editJustification}
            onChange={(e) => setEditJustification(e.target.value)}
          />
        </label>
        {editError && (
          <p className="text-color-error" style={{ marginTop: '8px' }}>{editError}</p>
        )}
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <ButtonBlack
            isDisabled={editing}
            label="UPDATE"
            onClick={handleEditConfirm}
          />
          <ButtonBlack
            label="CANCEL"
            onClick={closeEditModal}
          />
        </div>
      </Modal>

      <Modal
        buttonName="OK"
        isOpen={!!successMessage}
        title=""
        onClose={() => {
          setSuccessMessage(null);
          fetchSchedules(selectedRegion.value);
        }}
      >
        <p className="ta-sm-c">{successMessage}</p>
      </Modal>
    </main>
  );
};

export default Main;
